import {useEffect, useState} from "react";
import {useQuery} from "react-query";
import {RoleNameEnum} from "../../../../../utils/types/common";

import {UserService} from "../../../../../services/UserService";
import {Direction} from "../../../../../services/api/pagination";
import {useDebouncedPrimitiveValue} from "../../../../../hooks/useDebouncedPrimitiveValue";
import {PermissionsManagementFilters} from "../../permissions-management-filters";

type Params = {
  roles: ReadonlyArray<RoleNameEnum>;
  enabled?: boolean;
  filters?: PermissionsManagementFilters;
};

export const usePaginatedUserPermissionsQuery = ({
  roles,
  enabled = true,
  filters,
}: Params) => {
  const [page, setPage] = useState(0);

  const [sort, setSort] = useState<{
    direction: Direction;
    properties: string[];
  }>({
    direction: "ASC",
    properties: ["firstName", "lastName"],
  });

  const searchTerm = filters?.searchTerm ?? "";
  const roleFilters = filters?.roleFilters ?? [];
  const permissionsFilters = filters?.permissionsFilters ?? [];

  const debouncedSearchTerm = useDebouncedPrimitiveValue(searchTerm, 400);
  const trimmedSearch = debouncedSearchTerm.trim();

  const rolesForFetch = roleFilters.length > 0 ? roleFilters : roles;

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, roleFilters, permissionsFilters]);

  const query = useQuery(
    [
      "usersPermissionQuery",
      {
        page,
        size: 50,
        sort: sort.properties,
        direction: sort.direction,
        roles: rolesForFetch,
        search: trimmedSearch,
        permissionsFilters,
      },
    ],
    // eslint-disable-next-line
    async ({
      queryKey: [
        _,
        {roles: queryRoles, search, permissionsFilters: pf, ...params},
      ],
    }: any) => {
      const {data} = await UserService.fetchAll(
        [],
        queryRoles,
        [],
        params,
        search,
        pf,
      );

      return data;
    },
    {
      keepPreviousData: true,
      enabled,
    },
  );

  return {query, setSort, setPage, page};
};
